// ABOUTME: Module-level registry of calendar-item ids whose delete is optimistically applied but whose
// ABOUTME: 5-second undo-window server delete has not committed yet.
// The calendarItems cache read policy strips these ids, so no server-truth re-read (create, drag,
// refetch, poll, realtime event — any source) can resurrect a just-deleted card on screen during the
// undo window. Ids are cleared on undo and on error at once, and a few seconds after the commit.

const pendingDeleteIds = new Set<string>()
const graceTimers = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * How long a committed delete keeps filtering. A re-read started by a realtime
 * event just before the commit lands after it with the card still in the
 * answer; the grace covers that answer, and the delete's own event re-reads
 * the week meanwhile.
 */
export const PENDING_DELETE_GRACE_MS = 5000

const clearGrace = (id: string): void => {
  const timer = graceTimers.get(id)
  if (timer === undefined) return
  clearTimeout(timer)
  graceTimers.delete(id)
}

export const addPendingCalendarDelete = (id: string): void => {
  clearGrace(id)
  pendingDeleteIds.add(id)
}

/** Stops filtering at once — the card is back (undo) or was never gone (the delete failed). */
export const removePendingCalendarDelete = (id: string): void => {
  clearGrace(id)
  pendingDeleteIds.delete(id)
}

/**
 * The delete is committed on the server: keep filtering for the grace, then
 * forget the id.
 *
 * `onGraceEnd` is what makes the grace safe to leave. A stale week response
 * that arrived DURING it — the very case the grace exists for — was written to
 * the cache with the card still in it and merely hidden by this filter;
 * dropping the filter without collapsing that write again would put the card
 * back on screen. The caller re-applies its removal there.
 */
export const retirePendingCalendarDelete = (id: string, onGraceEnd?: () => void): void => {
  if (!pendingDeleteIds.has(id)) return
  clearGrace(id)
  graceTimers.set(
    id,
    setTimeout(() => {
      graceTimers.delete(id)
      pendingDeleteIds.delete(id)
      onGraceEnd?.()
    }, PENDING_DELETE_GRACE_MS),
  )
}

export const hasPendingCalendarDeletes = (): boolean => pendingDeleteIds.size > 0

/**
 * Returns `records` unchanged when nothing is pending (the hot path — zero cost and referentially
 * stable), otherwise a copy with any pending-delete ids filtered out.
 */
export const filterPendingCalendarDeletes = <T extends { id?: string | null }>(records: readonly T[]): readonly T[] => {
  if (pendingDeleteIds.size === 0) return records
  const filtered = records.filter((r) => r.id == null || !pendingDeleteIds.has(r.id))
  return filtered.length === records.length ? records : filtered
}

/**
 * What a field policy hands its `read`: enough of it to resolve a record's id.
 * Typed structurally rather than imported — Apollo's own `ReadFieldFunction` is
 * an overloaded signature, and this file is deliberately free of Apollo imports
 * (it is loaded by the type policies themselves).
 */
interface ReadOptions {
  readField?: unknown
}

/** Apollo's own `readField` is an overloaded signature; this is the shape used here. */
type ReadFieldByName = (fieldName: string, from: unknown) => unknown

/**
 * The id of a stored record, whichever form the store keeps it in: an inline
 * object today, a `Reference` once `CalendarItem` is normalized. `readField`
 * answers for both, so this read survives that switch — without it a reference
 * has no `id` of its own and every pending delete would quietly stop filtering.
 */
const recordId = (record: unknown, readField: ReadFieldByName | undefined): string | null | undefined => {
  if (readField) return readField("id", record) as string | null | undefined
  return (record as { id?: string | null }).id
}

/**
 * The `read` function for the calendarItems Apollo type policy: strips pending-delete records from
 * the stored field value. Hot path (nothing pending, or nothing to strip) returns the exact stored
 * value so Apollo's result identity is preserved and reads don't churn renders.
 */
export const readCalendarItemsStrippingPending = (existing: unknown, options?: ReadOptions): unknown => {
  if (!existing || pendingDeleteIds.size === 0) return existing
  const field = existing as { records?: readonly unknown[] }
  if (!Array.isArray(field.records)) return existing
  const records = field.records
  const readField = options?.readField as ReadFieldByName | undefined
  const filtered = records.filter((record) => {
    const id = recordId(record, readField)
    return id == null || !pendingDeleteIds.has(id)
  })
  return filtered.length === records.length ? existing : { ...field, records: filtered }
}
